/*!
 * devextreme-angular
 * Version: 24.2.7
 * Build date: Mon Apr 28 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */

/* tslint:disable:max-line-length */


import {
    TransferState,
    Component,
    NgModule,
    ElementRef,
    NgZone,
    PLATFORM_ID,
    Inject,

    Input,
    Output,
    OnDestroy,
    EventEmitter,
    OnChanges,
    DoCheck,
    SimpleChanges,
    ContentChildren,
    QueryList
} from '@angular/core';

export { ExplicitTypes } from 'devextreme/ui/list';

import DataSource from 'devextreme/data/data_source';
import { dxListItem, ItemDeleteMode, ListMenuMode, ContentReadyEvent, DisposingEvent, GroupRenderedEvent, InitializedEvent, ItemClickEvent, ItemContextMenuEvent, ItemDeletedEvent, ItemDeletingEvent, ItemHoldEvent, ItemRenderedEvent, ItemReorderedEvent, ItemSwipeEvent, OptionChangedEvent, PageLoadingEvent, PullRefreshEvent, ScrollEvent, SelectAllValueChangedEvent, SelectionChangedEvent, SelectionChangingEvent } from 'devextreme/ui/list';
import { DataSourceOptions } from 'devextreme/data/data_source';
import { Store } from 'devextreme/data/store';
import { dxSortableOptions } from 'devextreme/ui/sortable';
import { PageLoadMode, SearchMode, SelectAllMode, SingleMultipleAllOrNone, ScrollbarMode } from 'devextreme/common';
import { dxTextBoxOptions } from 'devextreme/ui/text_box';

import DxList from 'devextreme/ui/list';


import {
    DxComponent,
    DxTemplateHost,
    DxIntegrationModule,
    DxTemplateModule,
    NestedOptionHost,
    IterableDifferHelper,
    WatcherHelper
} from 'devextreme-angular/core';

import { DxoItemDraggingModule } from 'devextreme-angular/ui/nested';
import { DxoCursorOffsetModule } from 'devextreme-angular/ui/nested';
import { DxiItemModule } from 'devextreme-angular/ui/nested';
import { DxiMenuItemModule } from 'devextreme-angular/ui/nested';
import { DxoSearchEditorOptionsModule } from 'devextreme-angular/ui/nested';
import { DxiButtonModule } from 'devextreme-angular/ui/nested';
import { DxoOptionsModule } from 'devextreme-angular/ui/nested';

import { DxiListButtonModule } from 'devextreme-angular/ui/list/nested';
import { DxoListCursorOffsetModule } from 'devextreme-angular/ui/list/nested';
import { DxiListItemModule } from 'devextreme-angular/ui/list/nested';
import { DxoListItemDraggingModule } from 'devextreme-angular/ui/list/nested';
import { DxiListMenuItemModule } from 'devextreme-angular/ui/list/nested';
import { DxoListOptionsModule } from 'devextreme-angular/ui/list/nested';
import { DxoListSearchEditorOptionsModule } from 'devextreme-angular/ui/list/nested';

import { DxiItemComponent } from 'devextreme-angular/ui/nested';
import { DxiMenuItemComponent } from 'devextreme-angular/ui/nested';

import { DxiListItemComponent } from 'devextreme-angular/ui/list/nested';
import { DxiListMenuItemComponent } from 'devextreme-angular/ui/list/nested';


/**
 * The List is a UI component that represents a collection of items in a scrollable list.

 */
@Component({
    selector: 'dx-list',
    template: '',
    host: { ngSkipHydration: 'true' },
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost,
        IterableDifferHelper
    ]
})
export class DxListComponent<TItem = any, TKey = any> extends DxComponent implements OnDestroy, OnChanges, DoCheck {
    instance: DxList<TItem, TKey> = null;

    /**
     * Specifies the shortcut key that sets focus on the UI component.
    
     */
    @Input()
    get accessKey(): string | undefined {
        return this._getOption('accessKey');
    }
    set accessKey(value: string | undefined) {
        this._setOption('accessKey', value);
    }


    /**
     * Specifies whether the UI component changes its visual state as a result of user interaction.
    
     */
    @Input()
    get activeStateEnabled(): boolean {
        return this._getOption('activeStateEnabled');
    }
    set activeStateEnabled(value: boolean) {
        this._setOption('activeStateEnabled', value);
    }


    /**
     * Specifies whether or not an end user can delete list items.
    
     */
    @Input()
    get allowItemDeleting(): boolean {
        return this._getOption('allowItemDeleting');
    }
    set allowItemDeleting(value: boolean) {
        this._setOption('allowItemDeleting', value);
    }


    /**
     * A Boolean value specifying whether to enable or disable the bounce-back effect.
    
     */
    @Input()
    get bounceEnabled(): boolean {
        return this._getOption('bounceEnabled');
    }
    set bounceEnabled(value: boolean) {
        this._setOption('bounceEnabled', value);
    }


    /**
     * Specifies whether or not an end user can collapse groups.
    
     */
    @Input()
    get collapsibleGroups(): boolean {
        return this._getOption('collapsibleGroups');
    }
    set collapsibleGroups(value: boolean) {
        this._setOption('collapsibleGroups', value);
    }


    /**
     * Binds the UI component to data.
    
     */
    @Input()
    get dataSource(): Array<any | dxListItem | string> | DataSource | DataSourceOptions | null | Store | string {
        return this._getOption('dataSource');
    }
    set dataSource(value: Array<any | dxListItem | string> | DataSource | DataSourceOptions | null | Store | string) {
        this._setOption('dataSource', value);
    }


    /**
     * Specifies whether the UI component responds to user interaction.
    
     */
    @Input()
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Specifies the data field whose values should be displayed. Defaults to &apos;text&apos; when the data source contains objects.
    
     */
    @Input()
    get displayExpr(): ((item: any) => string) | string | undefined {
        return this._getOption('displayExpr');
    }
    set displayExpr(value: ((item: any) => string) | string | undefined) {
        this._setOption('displayExpr', value);
    }


    /**
     * Specifies the global attributes to be attached to the UI component&apos;s container element.
    
     */
    @Input()
    get elementAttr(): Record<string, any> {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: Record<string, any>) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies whether the UI component can be focused using keyboard navigation.
    
     */
    @Input()
    get focusStateEnabled(): boolean {
        return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value: boolean) {
        this._setOption('focusStateEnabled', value);
    }


    /**
     * Specifies whether data items should be grouped.
    
     */
    @Input()
    get grouped(): boolean {
        return this._getOption('grouped');
    }
    set grouped(value: boolean) {
        this._setOption('grouped', value);
    }


    /**
     * Specifies a custom template for group captions.
    
     */
    @Input()
    get groupTemplate(): any {
        return this._getOption('groupTemplate');
    }
    set groupTemplate(value: any) {
        this._setOption('groupTemplate', value);
    }


    /**
     * Specifies the UI component&apos;s height.
    
     */
    @Input()
    get height(): (() => number | string) | number | string | undefined {
        return this._getOption('height');
    }
    set height(value: (() => number | string) | number | string | undefined) {
        this._setOption('height', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the UI component.
    
     */
    @Input()
    get hint(): string | undefined {
        return this._getOption('hint');
    }
    set hint(value: string | undefined) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the UI component changes its state when a user pauses on it.
    
     */
    @Input()
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * Specifies whether or not to show the loading panel when the DataSource bound to the UI component is loading data.
    
     */
    @Input()
    get indicateLoading(): boolean {
        return this._getOption('indicateLoading');
    }
    set indicateLoading(value: boolean) {
        this._setOption('indicateLoading', value);
    }


    /**
     * Specifies the way a user can delete items from the list.
    
     */
    @Input()
    get itemDeleteMode(): ItemDeleteMode {
        return this._getOption('itemDeleteMode');
    }
    set itemDeleteMode(value: ItemDeleteMode) {
        this._setOption('itemDeleteMode', value);
    }


    /**
     * Configures item reordering using drag and drop gestures.
    
     */
    @Input()
    get itemDragging(): dxSortableOptions {
        return this._getOption('itemDragging');
    }
    set itemDragging(value: dxSortableOptions) {
        this._setOption('itemDragging', value);
    }


    /**
     * The time period in milliseconds before the onItemHold event is raised.
    
     */
    @Input()
    get itemHoldTimeout(): number {
        return this._getOption('itemHoldTimeout');
    }
    set itemHoldTimeout(value: number) {
        this._setOption('itemHoldTimeout', value);
    }


    /**
     * An array of items displayed by the UI component.
    
     */
    @Input()
    get items(): Array<any | dxListItem | string> {
        return this._getOption('items');
    }
    set items(value: Array<any | dxListItem | string>) {
        this._setOption('items', value);
    }


    /**
     * Specifies a custom template for items.
    
     */
    @Input()
    get itemTemplate(): any {
        return this._getOption('itemTemplate');
    }
    set itemTemplate(value: any) {
        this._setOption('itemTemplate', value);
    }


    /**
     * Specifies the key property that provides key values to access data items. Each key value must be unique.
    
     */
    @Input()
    get keyExpr(): Function | string {
        return this._getOption('keyExpr');
    }
    set keyExpr(value: Function | string) {
        this._setOption('keyExpr', value);
    }


    /**
     * Specifies the array of items for a context menu called for a list item.
    
     */
    @Input()
    get menuItems(): { action?: ((itemElement: any, itemData: any) => void), text?: string }[] {
        return this._getOption('menuItems');
    }
    set menuItems(value: { action?: ((itemElement: any, itemData: any) => void), text?: string }[]) {
        this._setOption('menuItems', value);
    }


    /**
     * Specifies whether an item context menu is shown when a user holds or swipes an item.
    
     */
    @Input()
    get menuMode(): ListMenuMode {
        return this._getOption('menuMode');
    }
    set menuMode(value: ListMenuMode) {
        this._setOption('menuMode', value);
    }


    /**
     * The text displayed on the button used to load the next page from the data source.
    
     */
    @Input()
    get nextButtonText(): string {
        return this._getOption('nextButtonText');
    }
    set nextButtonText(value: string) {
        this._setOption('nextButtonText', value);
    }


    /**
     * Specifies the text or HTML markup displayed by the UI component if the item collection is empty.
    
     */
    @Input()
    get noDataText(): string {
        return this._getOption('noDataText');
    }
    set noDataText(value: string) {
        this._setOption('noDataText', value);
    }


    /**
     * Specifies the text shown in the pullDown panel, which is displayed when the list is scrolled to the bottom.
    
     */
    @Input()
    get pageLoadingText(): string {
        return this._getOption('pageLoadingText');
    }
    set pageLoadingText(value: string) {
        this._setOption('pageLoadingText', value);
    }


    /**
     * Specifies whether the next page is loaded when a user scrolls the UI component to the bottom or when the &apos;next&apos; button is clicked.
    
     */
    @Input()
    get pageLoadMode(): PageLoadMode {
        return this._getOption('pageLoadMode');
    }
    set pageLoadMode(value: PageLoadMode) {
        this._setOption('pageLoadMode', value);
    }


    /**
     * Specifies the text displayed in the pullDown panel when the list is pulled below the refresh threshold.
    
     */
    @Input()
    get pulledDownText(): string {
        return this._getOption('pulledDownText');
    }
    set pulledDownText(value: string) {
        this._setOption('pulledDownText', value);
    }


    /**
     * Specifies the text shown in the pullDown panel while the list is being pulled down to the refresh threshold.
    
     */
    @Input()
    get pullingDownText(): string {
        return this._getOption('pullingDownText');
    }
    set pullingDownText(value: string) {
        this._setOption('pullingDownText', value);
    }


    /**
     * A Boolean value specifying whether or not the UI component supports the &apos;pull down to refresh&apos; gesture.
    
     */
    @Input()
    get pullRefreshEnabled(): boolean {
        return this._getOption('pullRefreshEnabled');
    }
    set pullRefreshEnabled(value: boolean) {
        this._setOption('pullRefreshEnabled', value);
    }


    /**
     * Specifies the text displayed in the pullDown panel while the list is being refreshed.
    
     */
    @Input()
    get refreshingText(): string {
        return this._getOption('refreshingText');
    }
    set refreshingText(value: string) {
        this._setOption('refreshingText', value);
    }


    /**
     * Specifies whether to repaint only those elements whose data changed.
    
     */
    @Input()
    get repaintChangesOnly(): boolean {
        return this._getOption('repaintChangesOnly');
    }
    set repaintChangesOnly(value: boolean) {
        this._setOption('repaintChangesOnly', value);
    }


    /**
     * Switches the UI component to a right-to-left representation.
    
     */
    @Input()
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * A Boolean value specifying if the list is scrolled by content.
    
     */
    @Input()
    get scrollByContent(): boolean {
        return this._getOption('scrollByContent');
    }
    set scrollByContent(value: boolean) {
        this._setOption('scrollByContent', value);
    }


    /**
     * Specifies whether a user can scroll the content with the scrollbar. Applies only if useNativeScrolling is false.
    
     */
    @Input()
    get scrollByThumb(): boolean {
        return this._getOption('scrollByThumb');
    }
    set scrollByThumb(value: boolean) {
        this._setOption('scrollByThumb', value);
    }


    /**
     * A Boolean value specifying whether to enable or disable list scrolling.
    
     */
    @Input()
    get scrollingEnabled(): boolean {
        return this._getOption('scrollingEnabled');
    }
    set scrollingEnabled(value: boolean) {
        this._setOption('scrollingEnabled', value);
    }


    /**
     * Configures the search panel.
    
     */
    @Input()
    get searchEditorOptions(): dxTextBoxOptions<any> {
        return this._getOption('searchEditorOptions');
    }
    set searchEditorOptions(value: dxTextBoxOptions<any>) {
        this._setOption('searchEditorOptions', value);
    }


    /**
     * Specifies whether the search panel is visible.
    
     */
    @Input()
    get searchEnabled(): boolean {
        return this._getOption('searchEnabled');
    }
    set searchEnabled(value: boolean) {
        this._setOption('searchEnabled', value);
    }


    /**
     * Specifies a data object&apos;s field name or an expression whose value is compared to the search string.
    
     */
    @Input()
    get searchExpr(): Array<Function | string> | Function | string {
        return this._getOption('searchExpr');
    }
    set searchExpr(value: Array<Function | string> | Function | string) {
        this._setOption('searchExpr', value);
    }


    /**
     * Specifies a comparison operation used to search UI component items.
    
     */
    @Input()
    get searchMode(): SearchMode {
        return this._getOption('searchMode');
    }
    set searchMode(value: SearchMode) {
        this._setOption('searchMode', value);
    }


    /**
     * Specifies a delay in milliseconds between when a user finishes typing, and the search is executed.
    
     */
    @Input()
    get searchTimeout(): number | undefined {
        return this._getOption('searchTimeout');
    }
    set searchTimeout(value: number | undefined) {
        this._setOption('searchTimeout', value);
    }


    /**
     * Specifies the current search string.
    
     */
    @Input()
    get searchValue(): string {
        return this._getOption('searchValue');
    }
    set searchValue(value: string) {
        this._setOption('searchValue', value);
    }


    /**
     * Specifies the mode in which all items are selected.
    
     */
    @Input()
    get selectAllMode(): SelectAllMode {
        return this._getOption('selectAllMode');
    }
    set selectAllMode(value: SelectAllMode) {
        this._setOption('selectAllMode', value);
    }


    /**
     * Specifies the text displayed at the &apos;Select All&apos; check box.
    
     */
    @Input()
    get selectAllText(): string {
        return this._getOption('selectAllText');
    }
    set selectAllText(value: string) {
        this._setOption('selectAllText', value);
    }


    /**
     * Specifies whether an item is selected if a user clicks it.
    
     */
    @Input()
    get selectByClick(): boolean {
        return this._getOption('selectByClick');
    }
    set selectByClick(value: boolean) {
        this._setOption('selectByClick', value);
    }


    /**
     * Specifies an array of currently selected item keys.
    
     */
    @Input()
    get selectedItemKeys(): Array<any> {
        return this._getOption('selectedItemKeys');
    }
    set selectedItemKeys(value: Array<any>) {
        this._setOption('selectedItemKeys', value);
    }


    /**
     * An array of currently selected item objects.
    
     */
    @Input()
    get selectedItems(): Array<any> {
        return this._getOption('selectedItems');
    }
    set selectedItems(value: Array<any>) {
        this._setOption('selectedItems', value);
    }


    /**
     * Specifies item selection mode.
    
     */
    @Input()
    get selectionMode(): SingleMultipleAllOrNone {
        return this._getOption('selectionMode');
    }
    set selectionMode(value: SingleMultipleAllOrNone) {
        this._setOption('selectionMode', value);
    }


    /**
     * Specifies when the UI component shows the scrollbar.
    
     */
    @Input()
    get showScrollbar(): ScrollbarMode {
        return this._getOption('showScrollbar');
    }
    set showScrollbar(value: ScrollbarMode) {
        this._setOption('showScrollbar', value);
    }


    /**
     * Specifies whether or not to display controls used to select list items. Not available if selectionMode is set to &apos;none&apos;.
    
     */
    @Input()
    get showSelectionControls(): boolean {
        return this._getOption('showSelectionControls');
    }
    set showSelectionControls(value: boolean) {
        this._setOption('showSelectionControls', value);
    }


    /**
     * Specifies the number of the element when the Tab key is used for navigating.
    
     */
    @Input()
    get tabIndex(): number {
        return this._getOption('tabIndex');
    }
    set tabIndex(value: number) {
        this._setOption('tabIndex', value);
    }


    /**
     * Specifies whether or not the UI component uses native scrolling.
    
     */
    @Input()
    get useNativeScrolling(): boolean {
        return this._getOption('useNativeScrolling');
    }
    set useNativeScrolling(value: boolean) {
        this._setOption('useNativeScrolling', value);
    }


    /**
     * Specifies whether the UI component is visible.
    
     */
    @Input()
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the UI component&apos;s width.
    
     */
    @Input()
    get width(): (() => number | string) | number | string | undefined {
        return this._getOption('width');
    }
    set width(value: (() => number | string) | number | string | undefined) {
        this._setOption('width', value);
    }

    /**
    
     * A function that is executed when the UI component is rendered and each time the component is repainted.
    
    
     */
    @Output() onContentReady: EventEmitter<ContentReadyEvent>;

    /**
    
     * A function that is executed before the UI component is disposed of.
    
    
     */
    @Output() onDisposing: EventEmitter<DisposingEvent>;

    /**
    
     * A function that is executed when a group element is rendered.
    
    
     */
    @Output() onGroupRendered: EventEmitter<GroupRenderedEvent>;

    /**
    
     * A function used in JavaScript frameworks to save the UI component instance.
    
    
     */
    @Output() onInitialized: EventEmitter<InitializedEvent>;

    /**
    
     * A function that is executed when a collection item is clicked or tapped.
    
    
     */
    @Output() onItemClick: EventEmitter<ItemClickEvent>;

    /**
    
     * A function that is executed when a collection item is right-clicked or pressed.
    
    
     */
    @Output() onItemContextMenu: EventEmitter<ItemContextMenuEvent>;

    /**
    
     * A function that is executed after a list item is deleted from the data source.
    
    
     */
    @Output() onItemDeleted: EventEmitter<ItemDeletedEvent>;

    /**
    
     * A function that is executed before a collection item is deleted from the data source.
    
    
     */
    @Output() onItemDeleting: EventEmitter<ItemDeletingEvent>;

    /**
    
     * A function that is executed when a collection item has been held for a specified period.
    
    
     */
    @Output() onItemHold: EventEmitter<ItemHoldEvent>;

    /**
    
     * A function that is executed after a collection item is rendered.
    
    
     */
    @Output() onItemRendered: EventEmitter<ItemRenderedEvent>;

    /**
    
     * A function that is executed after a list item is moved to another position.
    
    
     */
    @Output() onItemReordered: EventEmitter<ItemReorderedEvent>;

    /**
    
     * A function that is executed when a list item is swiped.
    
    
     */
    @Output() onItemSwipe: EventEmitter<ItemSwipeEvent>;

    /**
    
     * A function that is executed after a UI component property is changed.
    
    
     */
    @Output() onOptionChanged: EventEmitter<OptionChangedEvent>;

    /**
    
     * A function that is executed before the next page is loaded.
    
    
     */
    @Output() onPageLoading: EventEmitter<PageLoadingEvent>;

    /**
    
     * A function that is executed when the &apos;pull to refresh&apos; gesture is performed. Supported on mobile devices only.
    
    
     */
    @Output() onPullRefresh: EventEmitter<PullRefreshEvent>;

    /**
    
     * A function that is executed on each scroll gesture.
    
    
     */
    @Output() onScroll: EventEmitter<ScrollEvent>;

    /**
    
     * A function that is executed when the &apos;Select All&apos; check box value is changed. Applies only if the selectionMode is &apos;all&apos;.
    
    
     */
    @Output() onSelectAllValueChanged: EventEmitter<SelectAllValueChangedEvent>;

    /**
    
     * A function that is called after selection changes.
    
    
     */
    @Output() onSelectionChanged: EventEmitter<SelectionChangedEvent>;

    /**
    
     * A function that is called before selection changes.
    
    
     */
    @Output() onSelectionChanging: EventEmitter<SelectionChangingEvent>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() accessKeyChange: EventEmitter<string | undefined>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() activeStateEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() allowItemDeletingChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() bounceEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() collapsibleGroupsChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() dataSourceChange: EventEmitter<Array<any | dxListItem | string> | DataSource | DataSourceOptions | null | Store | string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() disabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() displayExprChange: EventEmitter<((item: any) => string) | string | undefined>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() elementAttrChange: EventEmitter<Record<string, any>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() focusStateEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() groupedChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() groupTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() heightChange: EventEmitter<(() => number | string) | number | string | undefined>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() hintChange: EventEmitter<string | undefined>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() hoverStateEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() indicateLoadingChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() itemDeleteModeChange: EventEmitter<ItemDeleteMode>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() itemDraggingChange: EventEmitter<dxSortableOptions>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() itemHoldTimeoutChange: EventEmitter<number>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() itemsChange: EventEmitter<Array<any | dxListItem | string>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() itemTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() keyExprChange: EventEmitter<Function | string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() menuItemsChange: EventEmitter<{ action?: ((itemElement: any, itemData: any) => void), text?: string }[]>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() menuModeChange: EventEmitter<ListMenuMode>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() nextButtonTextChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() noDataTextChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() pageLoadingTextChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() pageLoadModeChange: EventEmitter<PageLoadMode>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() pulledDownTextChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() pullingDownTextChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() pullRefreshEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() refreshingTextChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() repaintChangesOnlyChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() rtlEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() scrollByContentChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() scrollByThumbChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() scrollingEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() searchEditorOptionsChange: EventEmitter<dxTextBoxOptions<any>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() searchEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() searchExprChange: EventEmitter<Array<Function | string> | Function | string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() searchModeChange: EventEmitter<SearchMode>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() searchTimeoutChange: EventEmitter<number | undefined>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() searchValueChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() selectAllModeChange: EventEmitter<SelectAllMode>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() selectAllTextChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() selectByClickChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() selectedItemKeysChange: EventEmitter<Array<any>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() selectedItemsChange: EventEmitter<Array<any>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() selectionModeChange: EventEmitter<SingleMultipleAllOrNone>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() showScrollbarChange: EventEmitter<ScrollbarMode>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() showSelectionControlsChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() tabIndexChange: EventEmitter<number>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() useNativeScrollingChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() visibleChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() widthChange: EventEmitter<(() => number | string) | number | string | undefined>;




    @ContentChildren(DxiListItemComponent)
    get itemsChildren(): QueryList<DxiListItemComponent> {
        return this._getOption('items');
    }
    set itemsChildren(value) {
        this._setChildren('items', value, 'DxiListItemComponent');
    }

    @ContentChildren(DxiListMenuItemComponent)
    get menuItemsChildren(): QueryList<DxiListMenuItemComponent> {
        return this._getOption('menuItems');
    }
    set menuItemsChildren(value) {
        this._setChildren('menuItems', value, 'DxiListMenuItemComponent');
    }


    @ContentChildren(DxiItemComponent)
    get itemsLegacyChildren(): QueryList<DxiItemComponent> {
        return this._getOption('items');
    }
    set itemsLegacyChildren(value) {
        this._setChildren('items', value, 'DxiItemComponent');
    }

    @ContentChildren(DxiMenuItemComponent)
    get menuItemsLegacyChildren(): QueryList<DxiMenuItemComponent> {
        return this._getOption('menuItems');
    }
    set menuItemsLegacyChildren(value) {
        this._setChildren('menuItems', value, 'DxiMenuItemComponent');
    }




    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper,
            optionHost: NestedOptionHost,
            transferState: TransferState,
            @Inject(PLATFORM_ID) platformId: any) {

        super(elementRef, ngZone, templateHost, _watcherHelper, transferState, platformId);

        this._createEventEmitters([
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'groupRendered', emit: 'onGroupRendered' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'itemClick', emit: 'onItemClick' },
            { subscribe: 'itemContextMenu', emit: 'onItemContextMenu' },
            { subscribe: 'itemDeleted', emit: 'onItemDeleted' },
            { subscribe: 'itemDeleting', emit: 'onItemDeleting' },
            { subscribe: 'itemHold', emit: 'onItemHold' },
            { subscribe: 'itemRendered', emit: 'onItemRendered' },
            { subscribe: 'itemReordered', emit: 'onItemReordered' },
            { subscribe: 'itemSwipe', emit: 'onItemSwipe' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'pageLoading', emit: 'onPageLoading' },
            { subscribe: 'pullRefresh', emit: 'onPullRefresh' },
            { subscribe: 'scroll', emit: 'onScroll' },
            { subscribe: 'selectAllValueChanged', emit: 'onSelectAllValueChanged' },
            { subscribe: 'selectionChanged', emit: 'onSelectionChanged' },
            { subscribe: 'selectionChanging', emit: 'onSelectionChanging' },
            { emit: 'accessKeyChange' },
            { emit: 'activeStateEnabledChange' },
            { emit: 'allowItemDeletingChange' },
            { emit: 'bounceEnabledChange' },
            { emit: 'collapsibleGroupsChange' },
            { emit: 'dataSourceChange' },
            { emit: 'disabledChange' },
            { emit: 'displayExprChange' },
            { emit: 'elementAttrChange' },
            { emit: 'focusStateEnabledChange' },
            { emit: 'groupedChange' },
            { emit: 'groupTemplateChange' },
            { emit: 'heightChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'indicateLoadingChange' },
            { emit: 'itemDeleteModeChange' },
            { emit: 'itemDraggingChange' },
            { emit: 'itemHoldTimeoutChange' },
            { emit: 'itemsChange' },
            { emit: 'itemTemplateChange' },
            { emit: 'keyExprChange' },
            { emit: 'menuItemsChange' },
            { emit: 'menuModeChange' },
            { emit: 'nextButtonTextChange' },
            { emit: 'noDataTextChange' },
            { emit: 'pageLoadingTextChange' },
            { emit: 'pageLoadModeChange' },
            { emit: 'pulledDownTextChange' },
            { emit: 'pullingDownTextChange' },
            { emit: 'pullRefreshEnabledChange' },
            { emit: 'refreshingTextChange' },
            { emit: 'repaintChangesOnlyChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'scrollByContentChange' },
            { emit: 'scrollByThumbChange' },
            { emit: 'scrollingEnabledChange' },
            { emit: 'searchEditorOptionsChange' },
            { emit: 'searchEnabledChange' },
            { emit: 'searchExprChange' },
            { emit: 'searchModeChange' },
            { emit: 'searchTimeoutChange' },
            { emit: 'searchValueChange' },
            { emit: 'selectAllModeChange' },
            { emit: 'selectAllTextChange' },
            { emit: 'selectByClickChange' },
            { emit: 'selectedItemKeysChange' },
            { emit: 'selectedItemsChange' },
            { emit: 'selectionModeChange' },
            { emit: 'showScrollbarChange' },
            { emit: 'showSelectionControlsChange' },
            { emit: 'tabIndexChange' },
            { emit: 'useNativeScrollingChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {

        return new DxList(element, options);
    }


    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('dataSource', changes);
        this.setupChanges('items', changes);
        this.setupChanges('menuItems', changes);
        this.setupChanges('searchExpr', changes);
        this.setupChanges('selectedItemKeys', changes);
        this.setupChanges('selectedItems', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('dataSource');
        this._idh.doCheck('items');
        this._idh.doCheck('menuItems');
        this._idh.doCheck('searchExpr');
        this._idh.doCheck('selectedItemKeys');
        this._idh.doCheck('selectedItems');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
        super.clearChangedOptions();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }
}

@NgModule({
  imports: [
    DxoItemDraggingModule,
    DxoCursorOffsetModule,
    DxiItemModule,
    DxiMenuItemModule,
    DxoSearchEditorOptionsModule,
    DxiButtonModule,
    DxoOptionsModule,
    DxiListButtonModule,
    DxoListCursorOffsetModule,
    DxiListItemModule,
    DxoListItemDraggingModule,
    DxiListMenuItemModule,
    DxoListOptionsModule,
    DxoListSearchEditorOptionsModule,
    DxIntegrationModule,
    DxTemplateModule
  ],
  declarations: [
    DxListComponent
  ],
  exports: [
    DxListComponent,
    DxoItemDraggingModule,
    DxoCursorOffsetModule,
    DxiItemModule,
    DxiMenuItemModule,
    DxoSearchEditorOptionsModule,
    DxiButtonModule,
    DxoOptionsModule,
    DxiListButtonModule,
    DxoListCursorOffsetModule,
    DxiListItemModule,
    DxoListItemDraggingModule,
    DxiListMenuItemModule,
    DxoListOptionsModule,
    DxoListSearchEditorOptionsModule,
    DxTemplateModule
  ]
})
export class DxListModule { }

import type * as DxListTypes from "devextreme/ui/list_types";
export { DxListTypes };


